import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

export type UseScrollIntoViewOptions<T extends HTMLElement> = {
  ref: RefObject<T | null>;
  condition: boolean;
  behavior?: 'auto' | 'smooth';
  block?: 'start' | 'center' | 'end' | 'nearest';
  inline?: 'start' | 'center' | 'end' | 'nearest';
};

/**
 * 조건에 따라 특정 요소로 스크롤하는 hook
 *
 * @param ref - 스크롤 대상 요소의 ref
 * @param condition - 스크롤 실행 조건
 * @param behavior - 스크롤 동작 방식
 * @param block - 스크롤 블록 방향
 * @param inline - 스크롤 인라인 방향
 *
 * @example
 * ```tsx
 * const targetRef = useRef<HTMLDivElement>(null);
 * const previousPath = useLocationHistoryStore((state) => state.previousPath);
 *
 * useScrollIntoView({
 *   ref: targetRef,
 *   condition: previousPath?.includes('/table/') ?? false,
 *   behavior: 'smooth',
 * });
 *
 * return <div ref={targetRef}>...</div>;
 * ```
 */
export const useScrollIntoView = <T extends HTMLElement>({
  ref,
  condition,
  behavior = 'smooth',
  block = 'start',
  inline = 'nearest',
}: UseScrollIntoViewOptions<T>) => {
  const hasScrolled = useRef(false);

  useEffect(() => {
    if (condition && !hasScrolled.current && ref.current) {
      ref.current.scrollIntoView({ behavior, block, inline });
      hasScrolled.current = true;
    }

    if (!condition) {
      hasScrolled.current = false;
    }
  }, [condition, ref, behavior, block, inline]);
};
